import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Pencil, Plus, Trash2, Users, Calendar } from "lucide-react";
import { TopBar } from "@/components/layout/TopBar";
import { EditMemberDialog } from "@/components/roster/EditMemberDialog";
import { useCan } from "@/lib/roles";
import { PersonAvailabilityTable } from "@/components/roster/PersonAvailabilityTable";
import { useRoster, useDeleteMember } from "@/hooks/useRoster";
import { cn } from "@/lib/cn";
import { avatarTone } from "@/lib/format";
import type { TeamMember } from "@/types/roster";

const ROLE_LABELS: Record<string, string> = {
  pm: "Project Manager",
  ba: "Business Analyst",
  functional: "Functional",
  technical: "Technical",
  developer: "Developer",
  infrastructure: "Infrastructure",
  dba: "DBA",
  erp: "ERP Consultant",
  "wms consultant": "WMS Consultant",
};

interface TeamGroup {
  team: string;
  members: TeamMember[];
  totalCapacity: number;
}

// Tab taxonomy retired (May 2026):
//   - "Assignments" matrix: the binary check-cell grid is a relic of
//     the pre-hours-per-week assignment model. Mutations now happen on
//     each project/initiative's Team card.
//   - "Workload": every datapoint lives on /people/:name, TeamView, or
//     the Capacity heatmap. Three surfaces for the same data was one
//     too many.
//   - "Availability" stays — it's the only surface that answers
//     "when does someone free up?" (available_date / available_in_weeks).
type Tab = "teams" | "availability";

const TABS: { key: Tab; label: string; icon: typeof Users }[] = [
  { key: "teams", label: "Teams", icon: Users },
  { key: "availability", label: "Availability", icon: Calendar },
];

export function TeamRoster() {
  const { isPmo } = useCan();
  const { data: rawMembers, isLoading, isError, error } = useRoster();
  const members = Array.isArray(rawMembers) ? rawMembers : [];
  const deleteMember = useDeleteMember();

  const [tab, setTab] = useState<Tab>("teams");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMember, setEditMember] = useState<TeamMember | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const groups = useMemo<TeamGroup[]>(() => {
    const map = new Map<string, TeamMember[]>();
    for (const m of members) {
      const team = m.team || "Unassigned";
      if (!map.has(team)) map.set(team, []);
      map.get(team)!.push(m);
    }
    return Array.from(map.entries())
      .map(([team, members]) => ({
        team,
        members: members.sort((a, b) => a.name.localeCompare(b.name)),
        totalCapacity: members
          .filter((m) => m.include_in_capacity)
          .reduce((s, m) => s + m.project_capacity_hrs, 0),
      }))
      .sort((a, b) => a.team.localeCompare(b.team));
  }, [members]);

  const toggleCollapse = (team: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(team)) next.delete(team);
      else next.add(team);
      return next;
    });
  };

  const openNew = () => {
    setEditMember(null);
    setDialogOpen(true);
  };

  const openEdit = (m: TeamMember) => {
    setEditMember(m);
    setDialogOpen(true);
  };

  const handleDelete = () => {
    if (deleteConfirm) {
      deleteMember.mutate(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  return (
    <>
      <TopBar title="Roster" subtitle="Team members, capacity, and availability.">
        {tab === "teams" && isPmo && (
          <button
            onClick={openNew}
            className="flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-navy-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add member
          </button>
        )}
      </TopBar>

      {/* Tabs */}
      <div className="border-b border-line bg-surface px-8">
        <nav className="flex gap-1">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={cn(
                "flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors",
                tab === key
                  ? "border-brand-navy-600 text-brand-navy-700 dark:text-brand-navy-300"
                  : "border-transparent text-ink-muted hover:text-ink-2 hover:border-line-strong",
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="space-y-4 p-8">
        {isLoading && (
          <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
            Loading roster...
          </div>
        )}
        {isError && (
          <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-6 text-sm text-red-800 dark:text-red-300">
            {(error as Error).message}
          </div>
        )}

        {/* Tab: Teams */}
        {tab === "teams" && (
          <>
            {groups.map((group) => (
              <div
                key={group.team}
                className="overflow-hidden rounded-xl border border-line bg-surface"
              >
                <button
                  onClick={() => toggleCollapse(group.team)}
                  className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-raised transition-colors"
                >
                  <Users className="h-4 w-4 text-ink-subtle" />
                  <span className="text-sm font-semibold text-ink">
                    {group.team}
                  </span>
                  <span className="rounded-full bg-raised px-2 py-0.5 text-[10px] font-medium text-ink-muted">
                    {group.members.length}
                  </span>
                  <span className="ml-auto text-xs tabular-nums text-ink-subtle">
                    {group.totalCapacity.toFixed(0)} proj hrs/wk
                  </span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 text-ink-subtle transition-transform",
                      collapsed.has(group.team) && "-rotate-90",
                    )}
                  />
                </button>

                {!collapsed.has(group.team) && (
                  <div className="overflow-x-auto border-t border-line-soft">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-line-soft text-left text-xs font-medium text-ink-muted">
                          <th className="px-4 py-2">Name</th>
                          <th className="px-4 py-2">Role</th>
                          <th className="px-4 py-2">Vendor</th>
                          <th className="px-4 py-2 text-right">Weekly Hrs</th>
                          <th className="px-4 py-2 text-right">Support %</th>
                          <th className="px-4 py-2 text-right">Project %</th>
                          <th className="px-4 py-2 text-right">Project Hrs</th>
                          <th className="px-4 py-2 text-center">In Capacity</th>
                          <th className="px-4 py-2 w-16" />
                        </tr>
                      </thead>
                      <tbody>
                        {group.members.map((m) => (
                          <tr
                            key={m.name}
                            onClick={() => openEdit(m)}
                            className={cn(
                              "border-b border-line-soft transition-colors hover:bg-raised cursor-pointer",
                              !m.include_in_capacity && "opacity-50",
                            )}
                          >
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                <div
                                  className="flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-semibold"
                                  style={{ backgroundColor: avatarTone(m.name) }}
                                >
                                  {m.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .slice(0, 2)}
                                </div>
                                <Link
                                  to={`/people/${encodeURIComponent(m.name)}`}
                                  className="font-medium text-ink hover:text-brand-navy-700 dark:hover:text-brand-navy-300 hover:underline underline-offset-2 decoration-brand-navy-400"
                                >
                                  {m.name}
                                </Link>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-ink-3">
                              {ROLE_LABELS[m.role_key] ?? m.role}
                            </td>
                            <td className="px-4 py-2.5 text-ink-muted">
                              {m.vendor || "\u2014"}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-ink-2">
                              {m.weekly_hrs_available}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-ink-2">
                              {Math.round(m.support_reserve_pct * 100)}%
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-ink-2">
                              {Math.round(m.project_capacity_pct * 100)}%
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums font-medium text-ink">
                              {m.project_capacity_hrs.toFixed(1)}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span
                                className={cn(
                                  "inline-block h-2 w-2 rounded-full",
                                  m.include_in_capacity
                                    ? "bg-emerald-500"
                                    : "bg-muted-strong",
                                )}
                              />
                            </td>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center justify-end gap-1">
                                {isPmo && (<>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEdit(m);
                                  }}
                                  className="rounded p-1 text-ink-subtle hover:bg-brand-navy-50 dark:hover:bg-brand-navy-500/15 hover:text-brand-navy-700 dark:hover:text-brand-navy-300 transition-colors"
                                  aria-label="Edit member"
                                  title="Edit capacity & details"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteConfirm(m.name);
                                  }}
                                  className="rounded p-1 text-ink-faint hover:bg-red-50 dark:hover:bg-red-500/15 hover:text-red-500 transition-colors"
                                  aria-label="Delete member"
                                  title="Delete member"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                                </>)}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
            {!isLoading && members.length === 0 && (
              <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
                No team members found. Click "Add member" to get started.
              </div>
            )}
          </>
        )}

        {/* Tab: Availability */}
        {tab === "availability" && members.length > 0 && <PersonAvailabilityTable roster={members} />}
      </div>

      {/* Edit/Create Dialog */}
      <EditMemberDialog
        member={editMember}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-xl bg-surface p-6 shadow-lg">
            <h3 className="text-sm font-semibold text-ink">Delete team member?</h3>
            <p className="mt-2 text-sm text-ink-3">
              Remove <strong>{deleteConfirm}</strong> from the roster? This also removes their project assignments.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="rounded-lg border border-line px-3 py-1.5 text-sm font-medium text-ink-3 hover:bg-raised transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMember.isPending}
                className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleteMember.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
